import { memo, useState, useCallback, useMemo } from 'react';
import { cn, ButtonTabs, RawHtml } from '@divlab/divanui';

import Section from '@Components/Section';
import PecomDelivery from '@Components/PecomDelivery';
import ShippingCostCalculator from '@Components/ShippingCostCalculator';
import ServiceTable from '../ServiceTable';
import DeliveryTabContent from '../DeliveryTabContent';
import styles from './DeliverySection.module.css';

import type { PecomDeliveryParameters } from '@Components/PecomDelivery';
import type { DeliveryPageData } from '@Pages/PageProduct';
import type { FC, HTMLAttributes } from 'react';
import type { Tab } from '@divlab/divanui';

export interface DeliverySectionProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  title?: string;
  delivery?: DeliveryPageData;
  goodsInfo: PecomDeliveryParameters[];
}

const DeliverySection: FC<DeliverySectionProps> = (props) => {
  const { className, title, delivery: deliveryData, goodsInfo, ...restProps } = props;
  const tabs = deliveryData.tabs;
  const [selectedTab, setSelectedTab] = useState<Tab>(tabs.length > 0 ? tabs[0] : null);
  const handleChangeTab = useCallback((_e, tab: Tab) => {
    setSelectedTab(tab);
  }, []);

  const deliveryTitle = useMemo(() => {
    if (selectedTab?.id === 'delivery' && deliveryData.delivery?.title)
      return deliveryData.delivery.title;
    if (selectedTab?.id === 'assembling' && deliveryData.assembly?.title)
      return deliveryData.assembly.title;

    return title;
  }, [deliveryData.delivery?.title, deliveryData.assembly?.title, selectedTab, title]);

  return (
    <Section
      {...restProps}
      className={cn(styles.deliverySection, className)}
      title={<>{deliveryTitle}</>}
      additional={
        tabs.length > 1 && (
          <ButtonTabs defaultValue={selectedTab?.id} tabs={tabs} onChangeTab={handleChangeTab} />
        )
      }
    >
      <div className={styles.content}>
        {deliveryData.teaser && (
          <div className={styles.text}>
            <RawHtml html={deliveryData.teaser} className={styles.description} />
            {deliveryData.nuance && <div className={styles.nuance}>{deliveryData.nuance}</div>}
          </div>
        )}

        {selectedTab?.id === 'delivery' ? (
          <>
            {(() => {
              if (deliveryData.deliveryTypes) {
                return (
                  <PecomDelivery delivery={deliveryData.deliveryTypes} goodsInfo={goodsInfo} />
                );
              }

              return (
                <>
                  {deliveryData.delivery?.text && (
                    <div className={styles.deliveryText}>{deliveryData.delivery.text}</div>
                  )}

                  {deliveryData.needCalculator && <ShippingCostCalculator />}

                  {(deliveryData.tables?.delivery || deliveryData.tables?.uplift) && (
                    <DeliveryTabContent deliveryTables={deliveryData.tables} />
                  )}
                  {deliveryData.additionalCost && (
                    <div
                      className={styles.additionalCost}
                      dangerouslySetInnerHTML={{ __html: deliveryData.additionalCost }}
                    />
                  )}
                </>
              );
            })()}
          </>
        ) : (
          <>
            {deliveryData.assembly?.text && (
              <div className={styles.assemblyInfo}>
                {deliveryData.assembly.text.map((text, index) => (
                  <p key={index}>{text}</p>
                ))}
              </div>
            )}

            {deliveryData.tables?.assembly?.content &&
              deliveryData.tables.assembly.content.map((table, index) => (
                <div
                  className={cn(styles.table, {
                    [styles.hidden]: selectedTab?.id !== 'assembling',
                  })}
                  key={index}
                >
                  <ServiceTable contentData={table.options} />
                </div>
              ))}
          </>
        )}
      </div>
    </Section>
  );
};

export default memo(DeliverySection);
